import {
  type ExampleGroup,
  type ExampleGroupProvider,
  buildExampleGroup,
  selectExampleScenario,
} from '@splitfiesta/core';
import { i18n } from '@splitfiesta/i18n/web';

import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';

// Web stays on its own wording (it deploys ahead of the mobile build): the
// description keeps the hide hint and drops the cast sentence.
const WEB_KEYS: Record<string, string> = {
  description: 'descriptionWeb',
  descriptionPair: 'descriptionWeb',
};

/**
 * The example group for the signed-in user, or null when it is hidden or
 * nobody is signed in. Read fresh each time — it is tiny — so it always
 * reflects the current user, currency, language and launch count (the
 * scenario rotates one step per page load; see ExampleGroupSession).
 */
export function buildWebExampleGroup(): ExampleGroup | null {
  const ui = useUIStore.getState();
  if (ui.exampleGroupHidden) return null;
  const user = useAuthStore.getState().user;
  if (!user) return null;
  const scenario = selectExampleScenario(
    ui.exampleGroupLaunches,
    ui.exampleScenarioOverride
  );
  return buildExampleGroup({
    // Web has no device-currency detector: profile currency or USD.
    currency: user.settings?.currency || 'USD',
    you: { id: user.id, name: user.name || i18n.t('groups:exampleGroup.you') },
    t: (key, params) =>
      i18n.t(`groups:exampleGroup.${WEB_KEYS[key] ?? key}`, params),
    scenario,
    // The review-seed banners, served from public/.
    imageUrl: `/images/example-covers/${scenario.key}.jpg`,
  });
}

/** What `withExampleGroup` consults on every API call. */
export const exampleGroupProvider: ExampleGroupProvider = {
  // Namespaces load lazily over HTTP on web: make sure `groups` is in
  // before building, so served names are never bare keys.
  build: async () => {
    if (useUIStore.getState().exampleGroupHidden) return null;
    await i18n.loadNamespaces(['groups']);
    return buildWebExampleGroup();
  },
  onDelete: () => useUIStore.getState().setExampleGroupHidden(true),
  readOnlyMessage: () => i18n.t('groups:exampleGroup.readOnlyWeb'),
};
